/**
 * MyActionsCard - the Dashboard's "My tasks": the signed-in user's open tasks
 * on leads, opportunities AND automatic Customer check-ins (T-08), in one
 * list, overdue first (T-01 - "Tasks" everywhere, one list for leads and
 * opportunities, Paul Q10). Tasks on won projects are PM work and stay on the
 * separate Project tasks card. "See all tasks" opens the Tasks page (T-06).
 *
 * Paul: "if the next step isn't visible when I log in, it doesn't happen" -
 * so the card ALWAYS renders for anyone the API lets read tasks, with an
 * explicit empty state. It is hidden only when the API refuses (non-BD roles
 * get a 403). Each row opens its lead or opportunity; the box completes it
 * through the shared flow, so the last task on a record asks for the next
 * step (T-04), after the optional completion note (T-09).
 *
 * A My / All toggle (like Project tasks) lets a manager see the whole team's
 * lead and opportunity tasks; won-project tasks stay out of All.
 *
 * Mine is EVERYTHING assigned to me, won-project tasks included with a
 * Project label (Sharif, 10/8/26): Mohammed gave Maryam a task on a won
 * project and it showed only on the Project tasks card further down, so she
 * thought it never arrived. The Project tasks card is unchanged.
 *
 * Mine is every open task I own OR am tagged on (10/6/26); one I am on only
 * because I was tagged says "Tagged" and names its owner.
 *
 * A re-read that fails (a 429 or 502 while every open tab re-reads after
 * someone's change) keeps the card as it is; only a read the person is
 * waiting for - Mine / All, Try again - says it failed.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { fmtDate } from '../../utils/format';
import { accountLabel, indexById, isTaskOverdue, sortOverdueFirst, taskHash, taskKind } from './nextStep';
import { useTasksChanged } from '../tasks/useTasks';
import { useCompleteTask } from '../tasks/useCompleteTask';
import { localDay, TYPE_TAGS } from '../tasks/tasksTable';
import { latestReads } from '../../live/useLive';
import { TaggedChip, TaggedNames } from '../tasks/TagPeople';
import { taggedNotOwner, taggedOf } from '../tasks/tagged';
import { FILL_CARD, FILL_LABEL, FILL_SCROLL } from '../dashboard/cardStyles';

// 'Montserrat' is never loaded; inherit the app's Inter like the cards beside it
const FONT = 'inherit';
const SHOW = 8;
const GREEN = '#1B5E20';
const KIND_TAG = {
  lead: TYPE_TAGS.lead,
  opportunity: { ...TYPE_TAGS.opportunity, text: 'Opp' }, // short: the row is narrow
  'check-in': TYPE_TAGS['check-in'],
  project: TYPE_TAGS.project,
};

export default function MyActionsCard({ fill = false } = {}) {
  const { allAccounts, allProjects, loading, users } = useApp();
  const { user } = useAuth();
  // { scope, rows }: each list remembers which toggle it answers, so Mine's
  // rows never show under All while All loads (or could not). null = not
  // loaded / not allowed.
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(''); // the toggle whose read, one the person waited for, failed
  const [showAll, setShowAll] = useState(false);
  const [scope, setScope] = useState('mine'); // 'mine' | 'all'
  // "Loading…" only until the first load lands - every AppContext refresh flips loading again
  const [loadedOnce, setLoadedOnce] = useState(!loading);
  if (!loading && !loadedOnce) setLoadedOnce(true);
  const firstLoad = loading && !loadedOnce;
  const [reads] = useState(latestReads); // reads can overlap: the newest answer GOT wins
  const load = useCallback(
    ({ live } = {}) => {
      const ticket = reads.start();
      // every type in one pull; won-project tasks are filtered out below
      fetch(`/api/crm-actions?${scope === 'mine' ? 'owner=me&' : ''}status=open`)
        .then(async (r) => {
          if (r.status === 403) {
            if (reads.show(ticket)) setItems(null);
            return;
          } // the API refuses this role: no card
          const rows = r.ok ? await r.json() : null;
          if (!Array.isArray(rows)) throw new Error(`the server answered ${r.status}`);
          if (reads.show(ticket)) {
            setItems({ scope, rows });
            setFailed((f) => (f === scope ? '' : f));
          }
        })
        // the card stays as it is; someone else's change (live) re-reads quietly
        .catch(() => {
          if (!live && reads.show(ticket)) setFailed(scope);
        });
    },
    [scope, reads]
  );
  useEffect(load, [load]);
  useTasksChanged(load);
  const { complete, prompt } = useCompleteTask();

  const accountsById = useMemo(() => indexById(allAccounts), [allAccounts]);
  const projectsById = useMemo(() => indexById(allProjects), [allProjects]);
  if (items === null) return null;

  // the LOCAL day: the UTC one turned "due today" overdue after about 8pm Eastern
  const today = localDay();
  const current = items.scope === scope; // false just after Mine / All, until that list arrives
  const mine = current ? items.rows.filter((x) => items.scope === 'mine' || taskKind(x, projectsById) !== 'project') : [];
  const sorted = sortOverdueFirst(mine, today);
  const overdue = sorted.filter((x) => isTaskOverdue(x, today));
  const shown = showAll ? sorted : sorted.slice(0, SHOW);
  const headline = mine.length === 0 ? 'Nothing open' : overdue.length ? `${overdue.length} overdue` : 'On track';
  // a lead task reads "Company - Site" from the live record; the stored label otherwise
  const homeLabel = (x) => {
    if (x.entity_type === 'account') {
      const a = accountsById.get(String(x.entity_id));
      if (a) return accountLabel(a, accountsById);
    }
    return x.entity_label || '';
  };
  const open = (x) => {
    const h = taskHash(x, projectsById);
    if (h) window.location.hash = h;
  };

  return (
    // fill: one card that takes the whole Dashboard widget, its list scrolling inside
    <div style={fill ? { ...FILL_CARD, border: `1px solid ${overdue.length ? '#FFCDD2' : '#ECECEC'}` } : { marginBottom: 16 }}>
      <div
        style={
          fill
            ? { ...FILL_LABEL, fontFamily: FONT }
            : {
                fontSize: 9,
                fontWeight: 700,
                color: '#5A5A5A',
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                marginBottom: 8,
                fontFamily: FONT,
              }
        }
      >
        My tasks
      </div>
      <div
        style={
          fill
            ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', borderTop: '1px solid #EEE' }
            : { background: '#fff', border: `1px solid ${overdue.length ? '#FFCDD2' : '#E5E9DD'}`, borderRadius: 10, overflow: 'hidden' }
        }
      >
        <div
          style={{
            padding: '9px 16px',
            background: overdue.length ? '#FFF5F5' : '#F6F9F3',
            borderBottom: '1px solid #EEE',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
          }}
        >
          {!firstLoad && !current && failed === scope ? (
            <span role="alert" style={{ fontSize: 11, fontWeight: 800, color: '#B71C1C', fontFamily: FONT }}>
              Could not load these tasks.{' '}
              <button
                type="button"
                onClick={() => {
                  setFailed('');
                  load();
                }}
                style={{
                  padding: 0,
                  fontSize: 11,
                  fontWeight: 800,
                  color: GREEN,
                  fontFamily: FONT,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Try again
              </button>
            </span>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 800, color: overdue.length ? '#B71C1C' : '#1B5E20', fontFamily: FONT }}>
              {firstLoad || !current ? 'Loading…' : `${headline} · ${mine.length} open`}
            </span>
          )}
          <div style={{ display: 'inline-flex', borderRadius: 7, border: '1px solid #D8DED4', overflow: 'hidden', flexShrink: 0 }}>
            {[
              ['mine', 'Mine'],
              ['all', 'All'],
            ].map(([k, lbl]) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setFailed('');
                  setScope(k);
                }}
                aria-pressed={scope === k}
                title={k === 'mine' ? 'Tasks assigned to you, or that you are tagged on' : "Everyone's lead and opportunity tasks"}
                style={{
                  padding: '3px 10px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 9.5,
                  fontWeight: 700,
                  fontFamily: FONT,
                  background: scope === k ? GREEN : '#fff',
                  color: scope === k ? '#fff' : '#555',
                }}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
        <div style={fill ? FILL_SCROLL : undefined}>
          {!firstLoad && current && mine.length === 0 && (
            <div style={{ padding: '12px 16px', fontSize: 10.5, color: '#888', fontFamily: FONT, lineHeight: 1.5 }}>
              {scope === 'mine'
                ? 'No open tasks are assigned to you or tag you. Any task given to you shows up here - overdue ones in red.'
                : 'No open lead or opportunity tasks.'}
            </div>
          )}
          {!firstLoad &&
            shown.map((x) => {
              const late = isTaskOverdue(x, today);
              const kind = taskKind(x, projectsById);
              const tag = KIND_TAG[kind];
              const canOpen = !!taskHash(x, projectsById);
              const label = homeLabel(x);
              const taggedMe = taggedNotOwner(x, user, users);
              return (
                <div
                  key={x.id}
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    padding: '7px 16px',
                    borderBottom: '1px solid #F5F5F2',
                    fontFamily: FONT,
                  }}
                >
                  <button
                    type="button"
                    title="Mark complete"
                    aria-label={`Mark complete: ${x.description}`}
                    onClick={() => complete(x)}
                    style={{
                      flexShrink: 0,
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      cursor: 'pointer',
                      border: '1.5px solid #BBB',
                      background: '#fff',
                      padding: 0,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => open(x)}
                    disabled={!canOpen}
                    title={canOpen ? `Open ${label || 'the record'}` : undefined}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      gap: 10,
                      alignItems: 'center',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      textAlign: 'left',
                      cursor: canOpen ? 'pointer' : 'default',
                      fontFamily: FONT,
                    }}
                  >
                    <span
                      style={{
                        flex: '1 1 auto',
                        minWidth: 48,
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: '#0D0D0D',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {x.description}
                    </span>
                    {tag && (
                      <span
                        style={{
                          fontSize: 8.5,
                          fontWeight: 800,
                          color: tag.fg,
                          background: tag.bg,
                          borderRadius: 8,
                          padding: '1px 6px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {tag.text}
                      </span>
                    )}
                    {label && (
                      <span
                        style={{
                          fontSize: 9.5,
                          color: '#888',
                          whiteSpace: 'nowrap',
                          maxWidth: 150,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {label}
                      </span>
                    )}
                    {taggedMe && <TaggedChip />}
                    {(scope === 'all' || taggedMe) && x.owner && (
                      <span style={{ fontSize: 9.5, color: GREEN, fontWeight: 700, whiteSpace: 'nowrap' }}>{x.owner}</span>
                    )}
                    {scope === 'all' && <TaggedNames names={taggedOf(x)} style={{ fontSize: 9.5 }} />}
                    <span style={{ fontSize: 9.5, fontWeight: 800, color: late ? '#B71C1C' : '#1565C0', whiteSpace: 'nowrap' }}>
                      {late ? 'OVERDUE · ' : ''}
                      {fmtDate(x.due_date)}
                    </span>
                  </button>
                </div>
              );
            })}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: '6px 16px',
            ...(fill ? { borderTop: '1px solid #F5F5F2', flexShrink: 0 } : null),
          }}
        >
          {!firstLoad && sorted.length > SHOW ? (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              style={{
                padding: 0,
                fontSize: 9.5,
                color: GREEN,
                fontWeight: 700,
                fontFamily: FONT,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {showAll ? 'Show fewer' : `Show all ${sorted.length}`}
            </button>
          ) : (
            <span />
          )}
          <a
            href="#/tasks"
            title="Every person's tasks, with filters (the Tasks page)"
            style={{ fontSize: 9.5, color: GREEN, fontWeight: 700, fontFamily: FONT, textDecoration: 'none' }}
          >
            See all tasks
          </a>
        </div>
      </div>
      {prompt}
    </div>
  );
}
